import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../shared/Navbar';
import './LandingPage.css';
import './ElevenPlusLandingPage.css';
import { LearningChatMessage, LearningNav, LearningHero, HowItWorks, LearningFooter } from './LearningPageSections';

const exampleMessages: readonly LearningChatMessage[] = [
  {
    role: 'tutor',
    content: 'Look at this sequence: 3, 7, 11, 15. What changes each time?',
  },
  {
    role: 'student',
    content: 'It goes up by 4.',
  },
  {
    role: 'tutor',
    content: 'Exactly. Add 4 each time. What would the next two numbers be?',
  },
];

const COURSE_URL = '/course/60';
const REGISTER_URL = `/register?redirect=${encodeURIComponent(COURSE_URL)}`;

const subjects = [
  {
    title: 'Verbal Reasoning',
    description: 'Build vocabulary, logic and confidence with every question type.',
    icon: 'i-letters',
    route: '/course/47',
  },
  {
    title: 'Non-Verbal & Spatial',
    description: 'See patterns, rotations and 3D shapes with interactive teaching tools.',
    icon: 'i-shapes',
    route: '/course/46',
  },
  {
    title: 'English',
    description: 'Strengthen comprehension, grammar and the skills behind accurate answers.',
    icon: 'i-book-open',
    route: '/course/51',
  },
  {
    title: 'Mathematics',
    description: 'Learn reliable methods, then practise applying them to new questions.',
    icon: 'i-calculator',
    route: '/course/54',
  },
  {
    title: 'Writing',
    description: 'Plan, structure and improve creative writing with individual feedback.',
    icon: 'i-pencil',
    route: '/course/49',
  },
];

const examRoutes = [
  { title: 'Sutton', detail: 'Teaching and exam practice', route: '/course/45' },
  { title: 'Tiffin', detail: 'Teaching and exam practice', route: '/course/62' },
  { title: 'Reading', detail: 'FSCE familiarisation mocks', route: '/course/63' },
  { title: 'Kent', detail: 'Board-format exam practice', route: '/course/48' },
  { title: 'Medway', detail: 'Board-format exam practice', route: '/course/65' },
  { title: 'Bexley', detail: 'Board-format exam practice', route: '/course/56' },
  { title: 'Buckinghamshire', detail: 'Board-format exam practice', route: '/course/57' },
  { title: 'West Midlands', detail: 'Board-format exam practice', route: '/course/58' },
  { title: 'GL Assessment', detail: 'Full exam practice', route: '/course/59' },
];

export default function ElevenPlusLandingPage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = '11+ Preparation | TeachMeNow';

    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="lp-root learning-page eleven-plus-page">
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
        <defs>
          <symbol id="i-arrow-right" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
          </symbol>
          <symbol id="i-sparkles" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9.9 15.5A2 2 0 0 0 8.5 14l-6.1-1.5a.5.5 0 0 1 0-1L8.5 10A2 2 0 0 0 10 8.5l1.5-6.1a.5.5 0 0 1 1 0L14 8.5a2 2 0 0 0 1.5 1.5l6.1 1.5a.5.5 0 0 1 0 1L15.5 14a2 2 0 0 0-1.5 1.5l-1.5 6.1a.5.5 0 0 1-1 0Z" />
          </symbol>
          <symbol id="i-message" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z" />
            <path d="M8 9h8M8 13h5" />
          </symbol>
          <symbol id="i-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="m5 12 4 4L19 6" />
          </symbol>
          <symbol id="i-letters" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 19 9 5l5 14M6 14h6M14 9h6M17 6v6" />
          </symbol>
          <symbol id="i-shapes" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="7" cy="7" r="4" /><path d="m17 3 4 7h-8Z" /><rect x="13" y="14" width="8" height="7" rx="1" />
          </symbol>
          <symbol id="i-book-open" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 7v14M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3Z" />
          </symbol>
          <symbol id="i-calculator" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect width="16" height="20" x="4" y="2" rx="2" /><path d="M8 6h8M8 11h.01M12 11h.01M16 11h.01M8 15h.01M12 15h.01M16 15v4M8 19h.01M12 19h.01" />
          </symbol>
          <symbol id="i-pencil" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z" />
          </symbol>
        </defs>
      </svg>

      <Navbar />
      <LearningNav />

      <main>
        <LearningHero lessonTitle="Number patterns" messages={exampleMessages} title="Build confidence, one small step at a time."
          description="Prepare for the 11+ with ready-made courses in English, maths and reasoning. Each lesson follows prepared teaching content, with a patient AI tutor to explain the method, ask questions and give feedback along the way.">
          <Link className="tm-button tm-button--primary" to={COURSE_URL}>Explore the welcome course <span aria-hidden="true">→</span></Link>
          <a className="tm-button tm-button--secondary" href="#programme">Choose a subject</a>
        </LearningHero>
        <HowItWorks />

        <section className="block eleven-programme" id="programme">
          <div className="container">
            <div className="section-head">

              <h2>Build your 11+ skills</h2>
              <p>Choose a subject to explore, or start with the welcome course to find your starting point.</p>
            </div>
            <div className="eleven-subject-grid stagger">
              {subjects.map(subject => (
                <Link className="eleven-subject-card" to={subject.route} key={subject.title}>
                  <span className="icon-blob">
                    <svg className="icon-xl" aria-hidden="true"><use href={`#${subject.icon}`} /></svg>
                  </span>
                  <div>
                    <h3>{subject.title}</h3>
                    <p>{subject.description}</p>
                  </div>
                  <svg className="icon-md arrow" aria-hidden="true"><use href="#i-arrow-right" /></svg>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section id="exams" className="block eleven-exams">
          <div className="container eleven-exams-layout">
            <div className="eleven-exams-copy">

              <h2>Practise in your exam board&rsquo;s format</h2>
              <p>
                Once the teaching is in place, move into realistic exam practice.
                Dedicated routes cover local selection tests as well as GL Assessment.
              </p>
              <Link className="tm-button tm-button--primary btn btn-primary" to={COURSE_URL}>
                Explore the welcome course
                <svg className="icon-md" aria-hidden="true"><use href="#i-arrow-right" /></svg>
              </Link>
            </div>
            <div className="eleven-board-grid">
              {examRoutes.map(exam => (
                <Link to={exam.route} className="eleven-board-card" key={exam.title}>
                  <span>{exam.title}</span>
                  <small>{exam.detail}</small>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="closing eleven-closing">
          <div className="closing-inner">

            <h2>Ready to take the first step?</h2>
            <p>Create an account and explore the welcome course to see how TeachMeNow works.</p>
            <Link className="tm-button tm-button--primary btn btn-dark" to={REGISTER_URL}>
              Create an account
              <svg className="icon-md" aria-hidden="true"><use href="#i-arrow-right" /></svg>
            </Link>
          </div>
        </section>
      </main>

      <LearningFooter />
    </div>
  );
}
